import React from 'react';
import { Dropdown, IDropdownOption, mergeStyleSets } from '@fluentui/react';
import { X } from '@phosphor-icons/react';
import type { DashboardFilter } from '../../types/dashboard.types';

interface Props {
  filters: DashboardFilter[];
  onFilterChange: (filterId: string, value: string) => void;
  onRemoveFilter: (filterId: string) => void;
}

// Mirrors the governed dashboards' filter row (People Insights): a small
// uppercase label sits ABOVE each dropdown rather than dropdowns floating in
// a strip.
const s = mergeStyleSets({
  bar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 14,
    padding: '10px 20px 12px',
    background: '#FFFFFF',
    borderBottom: '1px solid #E1E1E1',
    flexWrap: 'wrap' as const,
    flexShrink: 0,
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 3,
  },
  filterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#9CA3AF',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
    whiteSpace: 'nowrap' as const,
  },
  filterItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#A0A0A0',
    padding: 2,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.12s',
    selectors: {
      ':hover': { color: '#D13438' },
    },
  },
});

export default function DashboardFilterBar({ filters, onFilterChange, onRemoveFilter }: Props) {
  if (filters.length === 0) return null;

  return (
    <div className={s.bar}>
      {filters.map((filter) => {
        const options: IDropdownOption[] = [
          { key: '', text: `All ${filter.label}` },
          ...filter.values.map((v) => ({ key: v, text: v })),
        ];
        return (
          <div key={filter.id} className={s.filterGroup}>
            <span className={s.filterLabel}>{filter.label}</span>
            <div className={s.filterItem}>
              <Dropdown
                selectedKey={filter.selectedValue || ''}
                options={options}
                onChange={(_, opt) => onFilterChange(filter.id, (opt?.key as string) || '')}
                styles={{
                  root: { minWidth: 160 },
                  dropdown: { fontSize: 13 },
                }}
                placeholder={filter.label}
              />
              <button className={s.removeBtn} onClick={() => onRemoveFilter(filter.id)} title="Remove filter">
                <X size={10} weight="regular" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
